import type { TableData, VisualizationData } from './types';
import { VISUALIZATION_TYPES } from './types';

const recordFromUnknown = (value: unknown): Record<string, unknown> | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const stringFromRecord = (
  raw: Record<string, unknown>,
  key: string
): string => {
  const value = raw[key];
  return typeof value === 'string' ? value : '';
};

const parseJsonWireField = (raw: unknown): unknown | null => {
  if (raw === undefined) {
    return null;
  }
  if (typeof raw !== 'string') {
    return raw;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Parses a BE `VisualizationData` wire payload into the FE structured-query
 * discriminated union. Returns `null` for unsupported visualization types or
 * malformed JSON-encoded payloads.
 */
export const parseVisualizationData = (
  raw: unknown
): VisualizationData | null => {
  const record = recordFromUnknown(raw);
  if (record === null) {
    return null;
  }

  const type = stringFromRecord(record, 'type');
  if (
    !(VISUALIZATION_TYPES as ReadonlyArray<string>).includes(type) ||
    !('dataJson' in record)
  ) {
    return null;
  }
  const dataJson = parseJsonWireField(record.dataJson);
  if (dataJson === null) {
    return null;
  }
  if (type === 'table') {
    return {
      type: 'table',
      dataJson: dataJson as TableData,
      ...(stringFromRecord(record, 'title') !== ''
        ? { title: stringFromRecord(record, 'title') }
        : {}),
      ...(record.configJson !== undefined
        ? { configJson: record.configJson }
        : {}),
    };
  }
  return {
    type: type as Exclude<VisualizationData['type'], 'table'>,
    dataJson,
    ...(stringFromRecord(record, 'title') !== ''
      ? { title: stringFromRecord(record, 'title') }
      : {}),
    ...(record.configJson !== undefined
      ? { configJson: record.configJson }
      : {}),
  };
};
